import React from "react";
import { motion } from "framer-motion";
import { useContent } from "../lib/content";

export const SectionHeader = ({ overline, title, kicker, align = "left" }) => (
  <div
    className={`max-w-3xl ${
      align === "center" ? "mx-auto text-center" : "text-left"
    }`}
  >
    {overline && (
      <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-5">
        {overline}
      </p>
    )}
    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#f4f0ec] font-light leading-[1.05] tracking-tight">
      {title}
    </h2>
    {kicker && (
      <p className="mt-6 text-base sm:text-lg text-[#a8a29e] leading-relaxed font-sans max-w-2xl">
        {kicker}
      </p>
    )}
  </div>
);

export const Reveal = ({ children, delay = 0, y = 30 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

export const PageHero = ({ overline, title, kicker, image }) => (
  <section
    data-testid="page-hero"
    className="relative pt-40 pb-24 lg:pt-48 lg:pb-32 overflow-hidden"
  >
    {image && (
      <div className="absolute inset-0">
        <img
          src={image}
          alt=""
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908]/40 via-[#0a0908]/70 to-[#0a0908]" />
      </div>
    )}
    <div className="relative max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
      <Reveal>
        <p className="text-[11px] font-mono uppercase tracking-[0.35em] text-[#d4af37] mb-6">
          {overline}
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl text-[#f4f0ec] font-light leading-[0.95] tracking-tight max-w-5xl">
          {title}
        </h1>
      </Reveal>
      {kicker && (
        <Reveal delay={0.2}>
          <p className="mt-8 text-lg sm:text-xl text-[#a8a29e] leading-relaxed max-w-2xl font-sans">
            {kicker}
          </p>
        </Reveal>
      )}
    </div>
  </section>
);

/** CMS-editable PageHero: every field reads from site_content `{prefix}.*` with the given defaults. */
export const CmsPageHero = ({ prefix, overline, titlePre, titleAccent, titlePost = "", kicker, image = "" }) => {
  const o = useContent(`${prefix}.overline`, overline);
  const tp = useContent(`${prefix}.title_pre`, titlePre);
  const ta = useContent(`${prefix}.title_accent`, titleAccent);
  const tpost = useContent(`${prefix}.title_post`, titlePost);
  const k = useContent(`${prefix}.kicker`, kicker);
  const img = useContent(`${prefix}.hero_image`, image);
  return (
    <PageHero
      overline={o}
      title={<>{tp}{ta && <span className="italic text-[#d4af37]">{ta}</span>}{tpost}</>}
      kicker={k}
      image={img || undefined}
    />
  );
};

export const Stat = ({ value, label, testId }) => (
  <div data-testid={testId} className="border-l border-[#d4af37]/40 pl-6">
    <div className="font-serif text-5xl sm:text-6xl text-[#d4af37] font-light leading-none">
      {value}
    </div>
    <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.25em] text-[#a8a29e]">
      {label}
    </p>
  </div>
);
